import { noop, kebabCase } from 'lodash';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Formik } from 'formik';
import { BucketFormikForm } from 'Src/types/forms';
import React, { Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { UnmountClosed } from 'react-collapse';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { TextLink } from 'Components/frameworks/text-link/TextLink';
import { COLORS } from 'Components/utils/color/colors';
import { RadioButton } from 'Components/frameworks/form-inputs/radio-button/RadioButton';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { getInsurancesStore } from 'Store/booking/booking.selector';
import createAction from 'Store/utils/createAction';
import { TOGGLE_TERMS } from 'Store/booking/proposals/constants';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { asLegacyDomNode, makeViewProps } from 'Src/types/view-helpers';
import { insuranceFieldName } from './insurance.utils';
import { ADD_INSURANCE, DELETE_INSURANCE } from './insurance.constantes';
type Props = {
  attachedDocuments: Array<{
    label: string;
    url: string;
  }>;
  id: string;
  insurance: UpsellInsurance;
  isLoading: boolean;
  onChange: (insurance: UpsellInsurance, action: string) => void;
  setAddedInsuranceNow: (value: boolean) => void;
  dataTestId?: string;
};

const FormatedInformationAndTerms = ({ attachedDocuments, isChecked }) => {
  const { isSeller } = useAppContext();
  return (
    <Fragment>
      <span
        className={classnames(
          'me-5',
          {
            'text-red': !isChecked,
          },
          {
            'text-black': isChecked,
          },
        )}
      >
        {t(
          isSeller
            ? 'booking.partners.insurance.termsAndCondition'
            : 'booking.insurance.addInsurance',
        )}
        :
      </span>
      {attachedDocuments.map(({ label, url }) => {
        return (
          <TextLink
            color={isChecked ? COLORS.BLACK : COLORS.RED}
            className="me-5"
            data-testid="insurance-cgv-pdf"
            key={label}
            label={label}
            url={url}
          />
        );
      })}
    </Fragment>
  );
};

export function InsuranceCTA({
  attachedDocuments,
  id,
  insurance,
  isLoading,
  onChange,
  setAddedInsuranceNow,
  dataTestId = '',
}: Props) {
  let isAdded = false;
  let termsChecked = false;
  let isDeleted = false;
  const insurancesStore = useSelector(getInsurancesStore);

  if (insurancesStore[id]) {
    isAdded = insurancesStore[id].isAdded;
    isDeleted = insurancesStore[id].isDeleted;
    termsChecked = insurancesStore[id].termsChecked;
  }

  const dispatch = useDispatch();
  const { trackClickEvent } = useTrackEvent();

  const refuseInsurance = () => {
    if (!isDeleted) {
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
        detail_click: 'remove_insurance',
      });
      onChange(insurance, DELETE_INSURANCE);
    }
  };

  const addInsuranceAction = () => {
    if (!isAdded && !insurance.pricingInfo.isPartiallyRented) {
      trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
        detail_click: 'add_insurance',
      });
      setAddedInsuranceNow(true);
      onChange(insurance, ADD_INSURANCE);
    }
  };

  return (
    <div className="px-20 pt-20" data-testid={dataTestId}>
      <div className="mb-20">
        <Formik
          initialValues={{
            [insuranceFieldName + id]: termsChecked,
          }}
          onSubmit={noop}
        >
          {() => (
            <BucketFormikForm>
              <div
                role="group"
                aria-labelledby="insurance-group"
                id={`form-${insuranceFieldName}${kebabCase(id)}`}
              >
                <div className="mb-10 grid grid-cols-2 items-center rounded-12 border-1 border-lightGrey bg-white px-20 py-10">
                  {t('booking.insurance.addInsuranceButton')}
                  <div className="flex items-center justify-end gap-10">
                    <p className="text-12 font-bold text-sienna md:text-14">
                      + {insurance.pricingInfo.totalPrice?.formattedValue}
                    </p>
                    {isLoading && <Spinner size="small" />}
                    <RadioButton
                      dataTestid="add-insurance-button"
                      isChecked={isAdded}
                      isDisabled={isLoading}
                      onClick={() => {
                        addInsuranceAction();
                      }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 items-center rounded-12 border-1 border-lightGrey bg-white px-20 py-10">
                  {t('booking.insurance.removeInsurance')}
                  <div className="flex justify-end gap-10">
                    {isLoading && <Spinner size="small" />}
                    <RadioButton
                      dataTestid="remove-insurance-button"
                      isChecked={isDeleted}
                      isDisabled={isLoading}
                      onClick={() => {
                        refuseInsurance();
                      }}
                    />
                  </div>
                </div>
                <UnmountClosed isOpened={isAdded}>
                  <BucketFormikForm>
                    <FormikInput
                      {...makeViewProps<any>({
                        className: 'mt-20 inline',
                        dataTestId: 'insuranceTerms',
                        label: asLegacyDomNode(
                          <FormatedInformationAndTerms
                            attachedDocuments={attachedDocuments}
                            isChecked={!isAdded || termsChecked || isDeleted}
                          />,
                        ),
                        name: insuranceFieldName + id,
                        onChange: () => {
                          dispatch(createAction(TOGGLE_TERMS, id));
                        },
                        type: 'checkbox',
                      })}
                    />
                  </BucketFormikForm>
                </UnmountClosed>
              </div>
            </BucketFormikForm>
          )}
        </Formik>
      </div>
    </div>
  );
}
